import { useAppStore } from '../../state/appStore';
import { useProgressStore } from '../../state/progressStore';
import { getLevel } from '../../levels';
import { carsByTransmission, getCar } from '../../sim/cars';
import { useT } from '../hooks';
import { BigButton, Panel, ScreenShell } from '../components';

export function LevelIntro() {
  const t = useT();
  const { activeLevelId, setScreen, startLevel, selectedCarId, selectCar } = useAppStore();
  const progress = useProgressStore((s) => s.progress);
  if (!activeLevelId) return null;
  const level = getLevel(activeLevelId);
  const carId = level.freeRoam && selectedCarId ? selectedCarId : level.car;
  const car = getCar(carId);

  return (
    <ScreenShell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="min-w-0 flex-1 break-words text-2xl font-bold sm:text-3xl">{t(level.titleKey)}</h2>
        <BigButton className="shrink-0" variant="ghost" onClick={() => setScreen(level.freeRoam ? 'menu' : 'levels')}>
          ← {t('intro.back')}
        </BigButton>
      </div>
      <p className="text-lg text-white/75">{t(level.descriptionKey)}</p>

      <Panel className="px-5 py-4">
        <div className="mb-1 text-sm uppercase tracking-wider text-white/50">{t('intro.car')}</div>
        {level.freeRoam ? (
          <div className="flex flex-wrap gap-2">
            {carsByTransmission(level.track).map((c) => {
              const unlocked = progress.unlockedCars.includes(c.id);
              const active = c.id === carId;
              return (
                <button
                  key={c.id}
                  disabled={!unlocked}
                  onClick={() => selectCar(c.id)}
                  title={unlocked ? '' : t('car.locked')}
                  className={`rounded-lg px-4 py-2 text-sm transition ${
                    active
                      ? 'bg-sky-500 font-semibold text-slate-950'
                      : 'bg-white/10 hover:bg-white/20'
                  } disabled:opacity-40`}
                >
                  {unlocked ? '' : '🔒 '}
                  {t(c.nameKey)}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-lg font-semibold" style={{ color: car.color }}>
            {t(car.nameKey)}
          </div>
        )}
      </Panel>

      {level.theoryKeys.length > 0 && (
        <Panel className="px-5 py-4">
          <div className="mb-3 text-sm uppercase tracking-wider text-white/50">
            📘 {t('intro.theory')}
          </div>
          <div className="flex flex-col gap-3">
            {level.theoryKeys.map((k) => (
              <div key={k}>
                <div className="font-semibold text-sky-300">{t(`${k}.title`)}</div>
                <p className="text-sm leading-relaxed text-white/70">{t(`${k}.body`)}</p>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {level.objectives.length > 0 && (
        <Panel className="px-5 py-4">
          <div className="mb-3 text-sm uppercase tracking-wider text-white/50">
            🎯 {t('intro.objectives')}
          </div>
          <ol className="flex list-decimal flex-col gap-1 pl-5 text-white/80">
            {level.objectives.map((o) => (
              <li key={o.id}>{t(o.titleKey)}</li>
            ))}
          </ol>
        </Panel>
      )}

      <BigButton onClick={startLevel} className="sticky bottom-0 z-10 w-full border border-sky-300/40 text-lg shadow-2xl shadow-slate-950/70">
        🚦 {t('intro.start')}
      </BigButton>
    </ScreenShell>
  );
}
